// Property-scoped Documents panel. Mounted by PropertyView via
// attachDocumentsPanel(). Owns its own fetch lifecycle: lists current
// (non-superseded) property_documents, lets a property manager upload a
// new file (kind = TITLE_DEED | AUTHORIZATION | OTHER), download/replace
// existing ones, and edit the OCR-extracted fields when present. For
// TITLE_DEED rows the extracted fields render as a structured form
// (owners[] with name + share, helyrajzi szám, address, area, etc.) with
// a one-click "Invite all listed owners as OWNER members" action that
// fans out to POST /properties/{id}/memberships.

import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { icon } from '../core/icons.js';
import { t } from '../core/i18n.js';
import { showFormError, clearFormError } from '../core/formError.js';
import { filePickerHtml, wireDropZones } from '../core/filePicker.js';

// TITLE_DEED moved to unit-scoped UnitDocumentsPanel; the property-level
// panel handles photos + authorization + generic files. PHOTO leads the
// list so it's the default selection — the common case is a landlord
// dropping property photos.
const KINDS = ['PHOTO', 'OTHER', 'AUTHORIZATION'];

const isImageMime = (m) => typeof m === 'string' && m.toLowerCase().startsWith('image/');

export class DocumentsPanel extends Component {
  constructor({ propertyId, canManage, view = 'all', onCount = null }) {
    super();
    this.propertyId = propertyId;
    this.canManage = !!canManage;
    // 'documents' shows only non-photo files, 'photos' only the image
    // gallery, 'all' shows both. Lets the property page split them into
    // Dokumentumok / Képek tabs off one fetch + one component.
    this.view = view;
    this.onCount = typeof onCount === 'function' ? onCount : null;
    this.basePath = `/properties/${propertyId}/documents`;
    this.state = {
      documents: null,
      error: null,
      busy: false,
      editingFieldsId: null,
      inviteBusyId: null,
      inviteSummary: null,
    };
    this._blobUrls = [];
  }

  destroy() {
    for (const u of this._blobUrls) {
      try { URL.revokeObjectURL(u); } catch {}
    }
    this._blobUrls = [];
  }

  // Split the rows into the photo gallery vs. everything else.
  _split(rows) {
    const all = Array.isArray(rows) ? rows : [];
    const photos = all.filter((r) => r.kind === 'PHOTO' && isImageMime(r.mime));
    const docs = all.filter((r) => !(r.kind === 'PHOTO' && isImageMime(r.mime)));
    return { photos, docs };
  }

  template() {
    const d = this.state.documents;
    const { photos, docs } = this._split(d || []);
    const isPhotos = this.view === 'photos';
    const count = isPhotos ? photos.length : (this.view === 'documents' ? docs.length : (d ? d.length : 0));
    const titleKey = isPhotos ? 'property.photos.title' : 'property.documents.title';
    const subKey = isPhotos ? 'property.photos.subtitle' : 'property.documents.subtitle';
    return `
      <div data-documents-panel>
        <div class="section-head">
          <div class="section-head-titles">
            <div class="section-head-eyebrow">${esc(t(titleKey) || t('property.documents.title'))}</div>
            <div class="section-head-count mono">${esc(String(count))}</div>
          </div>
          <p class="section-head-sub faint">${esc(t(subKey) || t('property.documents.subtitle'))}</p>
        </div>
        ${this.state.error ? `<div class="error-banner">${esc(this.state.error)}</div>` : ''}
        ${this.state.inviteSummary ? `<div class="info-banner">${esc(this.state.inviteSummary)}</div>` : ''}
        ${this.canManage ? this._uploadFormHtml() : ''}
        ${d === null
          ? `<div class="muted">${esc(t('property.documents.loading'))}</div>`
          : this._contentHtml(d)}
      </div>
    `;
  }

  // Photos render as a responsive thumbnail grid; everything else (the
  // authorization letter, generic files) stays as the document list. The
  // `view` prop narrows which half this instance shows.
  _contentHtml(rows) {
    const { photos, docs } = this._split(rows);
    const showPhotos = this.view !== 'documents';
    const showDocs = this.view !== 'photos';
    const empty = (showPhotos && showDocs) ? rows.length === 0
      : (showPhotos ? photos.length === 0 : docs.length === 0);
    if (empty) return `<div class="empty">${esc(t(this.view === 'photos' ? 'property.photos.empty' : 'property.documents.empty') || '')}</div>`;
    return `
      ${showPhotos && photos.length ? `<div class="doc-gallery">${photos.map((r) => this._photoTileHtml(r)).join('')}</div>` : ''}
      ${showDocs && docs.length ? this._listHtml(docs) : ''}
    `;
  }

  _photoTileHtml(row) {
    return `
      <figure class="doc-photo" data-doc-id="${esc(row.id)}">
        <img class="doc-photo-img" data-doc-thumb="${esc(row.id)}" alt="${esc(row.original_name || '')}" />
        <figcaption class="doc-photo-name">${esc(row.original_name || '')}</figcaption>
        <div class="doc-photo-actions">
          <button type="button" class="doc-photo-btn" data-action="download-doc" data-id="${esc(row.id)}" aria-label="${esc(t('common.download'))}">${icon('download', { size: 'sm' })}</button>
          ${this.canManage ? `<button type="button" class="doc-photo-btn danger" data-action="delete-doc" data-id="${esc(row.id)}" aria-label="${esc(t('common.delete'))}">${icon('trash', { size: 'sm' })}</button>` : ''}
        </div>
      </figure>
    `;
  }

  _uploadFormHtml() {
    const isPhotos = this.view === 'photos';
    // Photos tab uploads images as PHOTO (no kind picker). Documents tab
    // offers the non-photo kinds. 'all' keeps the full picker.
    const accept = isPhotos
      ? 'image/jpeg,image/png,image/webp,image/heic,image/gif'
      : 'image/jpeg,image/png,image/webp,image/heic,image/gif,application/pdf';
    const kindOptions = (this.view === 'documents' ? KINDS.filter((k) => k !== 'PHOTO') : KINDS);
    return `
      <form class="stack doc-upload-form" data-upload-form enctype="multipart/form-data">
        <div data-form-error></div>
        ${filePickerHtml({
          inputAttrs: `name="file" accept="${accept}" required data-role="docs-file"`,
          disabled: this.state.busy,
          variant: 'drop-zone',
          title: (isPhotos ? (t('property.photos.dropzone_title') || t('property.documents.dropzone_title')) : t('property.documents.dropzone_title')) || t('common.drop_or_browse'),
          subLabel: t('property.documents.file_sub'),
        })}
        <div class="doc-upload-meta" data-upload-meta hidden>
          <div class="row" style="gap:var(--space-3); flex-wrap:wrap; align-items:flex-end">
            ${isPhotos
              ? '<input type="hidden" name="kind" value="PHOTO" />'
              : `<div class="field" style="max-width:240px">
              <label>${esc(t('property.documents.kind'))}</label>
              <select class="select" name="kind">
                ${kindOptions.map((k) => `<option value="${k}">${esc(t('property.documents.kinds.' + k))}</option>`).join('')}
              </select>
            </div>`}
            <div class="field grow">
              <label>${esc(t('property.documents.label'))} <span class="faint">(${esc(t('common.optional'))})</span></label>
              <input class="input" name="label" maxlength="190" placeholder="${esc(t('property.documents.label_placeholder'))}" />
            </div>
            <button type="submit" class="btn btn-primary" ${this.state.busy ? 'disabled' : ''}>
              ${this.state.busy ? '<span class="spinner"></span> ' : ''}${esc(this.state.busy ? t('property.documents.uploading') : t('property.documents.upload'))}
            </button>
          </div>
        </div>
      </form>
    `;
  }

  _listHtml(rows) {
    return `<div class="stack">${rows.map((r, i) => this._rowHtml(r, i)).join('')}</div>`;
  }

  _rowHtml(row, index) {
    const sizeKb = Math.max(1, Math.round((row.size_bytes || 0) / 1024));
    const editing = this.state.editingFieldsId === row.id;
    const num = String((index ?? 0) + 1).padStart(2, '0');
    const kindLabel = esc(t('property.documents.kinds.' + row.kind));
    const created = row.created_at ? String(row.created_at).slice(0, 10) : '';
    const metaBits = [esc(row.original_name), `${sizeKb} KB`];
    if (created) metaBits.push(created);
    const thumb = isImageMime(row.mime)
      ? `<img class="doc-thumb" data-doc-thumb="${esc(row.id)}" alt="${esc(row.original_name || '')}" />`
      : '';
    return `
      <div class="unit-card" data-doc-id="${esc(row.id)}">
        <div class="unit-card-head">
          ${thumb || `<span class="unit-card-num mono">${num}</span>`}
          <div class="unit-card-info">
            <div class="unit-card-name">
              <strong>${kindLabel}</strong>${row.label ? `<em class="unit-card-summary">· ${esc(row.label)}</em>` : ''}
            </div>
            <div class="unit-card-meta-line mono">${metaBits.join(' · ').toUpperCase()}</div>
          </div>
          <div class="unit-card-actions">
            <button class="btn-mono" data-action="download-doc" data-id="${esc(row.id)}">${esc(t('common.download'))}</button>
            ${this.canManage ? `
              <button class="btn-mono danger" data-action="delete-doc" data-id="${esc(row.id)}"
                      aria-label="${esc(t('common.delete'))}">${icon('trash', { size: 'sm' })}</button>` : ''}
          </div>
        </div>
        ${row.kind === 'TITLE_DEED' && this.canManage ? this._extractedHtml(row, editing) : ''}
      </div>
    `;
  }

  _extractedHtml(row, editing) {
    const fields = row.extracted_fields || null;
    if (fields === null && !editing) {
      return `
        <div class="row" style="gap:var(--space-2); flex-wrap:wrap">
          <span class="faint">${esc(t('property.documents.no_extraction'))}</span>
          <button class="btn btn-ghost btn-sm" data-action="edit-fields" data-id="${esc(row.id)}">${esc(t('property.documents.add_fields_manually'))}</button>
        </div>
      `;
    }
    if (editing) return this._fieldsFormHtml(row, fields || {});
    return this._fieldsViewHtml(row, fields);
  }

  _fieldsViewHtml(row, f) {
    const ownersHtml = Array.isArray(f.owners) && f.owners.length > 0
      ? `<ol class="stack-2" style="margin:0; padding-left:1.25em">${f.owners.map((o) => `
          <li><strong>${esc(o.name || '—')}</strong>${o.share ? ` <span class="badge">${esc(o.share)}</span>` : ''}${o.address ? ` <span class="faint">— ${esc(o.address)}</span>` : ''}</li>
        `).join('')}</ol>`
      : `<span class="faint">${esc(t('property.documents.owners_empty'))}</span>`;
    const scalars = [
      ['helyrajzi_szam', f.helyrajzi_szam],
      ['address', f.address],
      ['area_sqm', f.area_sqm],
      ['ownership_form', f.ownership_form],
      ['document_date', f.document_date],
    ].filter(([, v]) => v !== undefined && v !== null && v !== '');
    return `
      <div class="stack-2">
        <div class="row-between">
          <strong>${esc(t('property.documents.extracted_title'))}</strong>
          <div class="row" style="gap:var(--space-2)">
            <button class="btn btn-ghost btn-sm" data-action="edit-fields" data-id="${esc(row.id)}">${icon('pencil', { size: 'sm' })}<span>${esc(t('common.edit'))}</span></button>
            ${Array.isArray(f.owners) && f.owners.length > 0
              ? `<button class="btn btn-primary btn-sm" data-action="import-owners" data-id="${esc(row.id)}" ${this.state.inviteBusyId === row.id ? 'disabled' : ''}>${icon('download', { size: 'sm' })}<span>${esc(this.state.inviteBusyId === row.id ? t('property.documents.importing_owners') : t('property.documents.import_owners'))}</span></button>`
              : ''}
          </div>
        </div>
        <dl class="stack-2">
          ${scalars.map(([k, v]) => `<div class="row" style="gap:var(--space-2)"><dt class="faint" style="min-width:160px">${esc(t('property.documents.fields.' + k))}</dt><dd>${esc(String(v))}</dd></div>`).join('')}
        </dl>
        <div><span class="faint">${esc(t('property.documents.owners_title'))}</span>${ownersHtml}</div>
        ${Array.isArray(f.encumbrances) && f.encumbrances.length > 0
          ? `<div><span class="faint">${esc(t('property.documents.fields.encumbrances'))}</span><ul style="margin:0; padding-left:1.25em">${f.encumbrances.map((e) => `<li>${esc(String(e))}</li>`).join('')}</ul></div>`
          : ''}
      </div>
    `;
  }

  _fieldsFormHtml(row, f) {
    const owners = Array.isArray(f.owners) ? f.owners : [];
    const ownerRows = owners.map((o, i) => `
      <div class="stack-2" data-owner-row="${i}" style="padding:var(--space-2) 0; border-top:1px solid var(--border)">
        <div class="row" style="gap:var(--space-2); align-items:center">
          <input class="input grow" data-owner-field="name" data-i="${i}" placeholder="${esc(t('property.documents.fields.owner_name'))}" value="${esc(o.name || '')}" />
          <input class="input" style="max-width:110px" data-owner-field="share" data-i="${i}" placeholder="1/2" value="${esc(o.share || '')}" />
          <button type="button" class="btn btn-ghost btn-icon" data-action="remove-owner" data-i="${i}" aria-label="${esc(t('common.remove'))}">${icon('trash', { size: 'sm' })}</button>
        </div>
        <div class="row" style="gap:var(--space-2)">
          <input class="input grow" data-owner-field="address" data-i="${i}" placeholder="${esc(t('property.documents.fields.owner_address'))}" value="${esc(o.address || '')}" />
          <input class="input grow" data-owner-field="email" data-i="${i}" type="email" placeholder="${esc(t('property.documents.fields.owner_email'))}" value="${esc(o.email || '')}" />
        </div>
      </div>
    `).join('');
    return `
      <form class="stack" data-fields-form data-id="${esc(row.id)}">
        <div data-form-error></div>
        <strong>${esc(t('property.documents.extracted_title'))}</strong>
        <div class="row" style="gap:var(--space-3); flex-wrap:wrap">
          <div class="field" style="max-width:220px"><label>${esc(t('property.documents.fields.helyrajzi_szam'))}</label><input class="input" name="helyrajzi_szam" value="${esc(f.helyrajzi_szam || '')}" /></div>
          <div class="field grow"><label>${esc(t('property.documents.fields.address'))}</label><input class="input" name="address" value="${esc(f.address || '')}" /></div>
        </div>
        <div class="row" style="gap:var(--space-3); flex-wrap:wrap">
          <div class="field" style="max-width:140px"><label>${esc(t('property.documents.fields.area_sqm'))}</label><input class="input" name="area_sqm" type="number" step="0.01" min="0" value="${esc(f.area_sqm ?? '')}" /></div>
          <div class="field grow"><label>${esc(t('property.documents.fields.ownership_form'))}</label><input class="input" name="ownership_form" value="${esc(f.ownership_form || '')}" /></div>
          <div class="field" style="max-width:170px"><label>${esc(t('property.documents.fields.document_date'))}</label><input class="input" name="document_date" type="date" value="${esc(f.document_date || '')}" /></div>
        </div>
        <div class="stack-2">
          <div class="row-between"><label>${esc(t('property.documents.owners_title'))}</label>
            <button type="button" class="btn btn-ghost btn-sm" data-action="add-owner">${icon('plus', { size: 'sm' })}<span>${esc(t('property.documents.add_owner'))}</span></button>
          </div>
          <div data-owners-list>${ownerRows}</div>
        </div>
        <div class="field"><label>${esc(t('property.documents.fields.encumbrances'))} <span class="faint">(${esc(t('property.documents.one_per_line'))})</span></label><textarea class="input" name="encumbrances" rows="3">${esc((Array.isArray(f.encumbrances) ? f.encumbrances : []).join('\n'))}</textarea></div>
        <div class="row" style="justify-content:flex-end; gap:var(--space-3)">
          <button type="button" class="btn btn-ghost" data-action="cancel-edit-fields">${esc(t('common.cancel'))}</button>
          <button type="submit" class="btn btn-primary">${esc(t('common.save'))}</button>
        </div>
      </form>
    `;
  }

  mount() {
    this.on('[data-action="download-doc"]', 'click', (e) => this._download(e.currentTarget.getAttribute('data-id')));
    this.on('[data-action="delete-doc"]', 'click', (e) => this._delete(e.currentTarget.getAttribute('data-id')));
    this.on('[data-action="edit-fields"]', 'click', (e) => this.setState({ editingFieldsId: e.currentTarget.getAttribute('data-id'), error: null }));
    this.on('[data-action="cancel-edit-fields"]', 'click', () => this.setState({ editingFieldsId: null, error: null }));
    this.on('[data-action="add-owner"]', 'click', () => this._addOwnerRow());
    this.on('[data-action="remove-owner"]', 'click', (e) => this._removeOwnerRow(Number(e.currentTarget.getAttribute('data-i'))));
    this.on('[data-action="import-owners"]', 'click', (e) => this._importOwners(e.currentTarget.getAttribute('data-id')));

    const uploadForm = this.el.querySelector('[data-upload-form]');
    if (uploadForm) uploadForm.addEventListener('submit', (e) => this._upload(e));
    const fieldsForm = this.el.querySelector('[data-fields-form]');
    if (fieldsForm) fieldsForm.addEventListener('submit', (e) => this._saveFields(e));

    // File picker: update the visible filename + clear button in place
    // without triggering a re-render so input.files survives until submit.
    const fileInput = this.el.querySelector('[data-role="docs-file"]');
    if (fileInput) fileInput.addEventListener('change', (e) => this._syncPickerLabel(e.target));
    this.on('[data-action="clear-file"]', 'click', (e) => {
      e.preventDefault();
      const inp = this.el.querySelector('[data-role="docs-file"]');
      if (!inp) return;
      inp.value = '';
      this._syncPickerLabel(inp);
    });

    if (this.state.documents === null && !this._loadStarted) {
      this._loadStarted = true;
      this._load();
    }
    wireDropZones(this.el);
    this._loadThumbnails();
  }

  // Lazily fill in image thumbnails. The /file endpoint needs the auth
  // header, so we can't point <img src> straight at it — fetch each as a
  // blob through the api client and swap in an object URL. Tracked in
  // _blobUrls so destroy() revokes them.
  async _loadThumbnails() {
    const imgs = this.el ? this.el.querySelectorAll('[data-doc-thumb]') : [];
    for (const img of imgs) {
      if (img.dataset.thumbLoaded) continue;
      img.dataset.thumbLoaded = '1';
      const id = img.getAttribute('data-doc-thumb');
      try {
        const blob = await api.get(`${this.basePath}/${id}/file`, { responseType: 'blob' });
        const url = URL.createObjectURL(blob);
        this._blobUrls.push(url);
        img.src = url;
      } catch { /* leave the broken-image alt text */ }
    }
  }

  async _load() {
    try {
      const r = await api.get(this.basePath);
      this.setState({ documents: (r && r.data) || [], error: null });
      this._reportCount();
    } catch (err) {
      this.setState({ documents: [], error: err instanceof ApiError ? err.message : t('property.documents.errors.load') });
    }
  }

  // Report the documents/photos split up to the host (property page tab
  // badges) after every load / mutation.
  _reportCount() {
    if (!this.onCount) return;
    const { photos, docs } = this._split(this.state.documents || []);
    this.onCount({ documents: docs.length, photos: photos.length, total: (this.state.documents || []).length });
  }

  // Reflect the picked filename in the custom .file-picker label without
  // triggering a re-render — re-rendering would empty input.files and the
  // FormData-based submit would lose the upload.
  _syncPickerLabel(input) {
    const picker = input.closest('.file-picker');
    if (!picker) return;
    const file = input.files && input.files[0];
    const nameEl = picker.querySelector('.file-picker-name');
    if (nameEl) {
      if (file) {
        nameEl.textContent = file.name;
        nameEl.classList.remove('is-empty');
      } else {
        nameEl.textContent = t('common.no_file_selected');
        nameEl.classList.add('is-empty');
      }
    }
    picker.classList.toggle('is-filled', !!file);
    let clearBtn = picker.querySelector('.file-picker-clear');
    if (file && !clearBtn) {
      picker.insertAdjacentHTML('beforeend',
        `<button type="button" class="file-picker-clear" data-action="clear-file" aria-label="${t('common.clear_file')}">×</button>`);
    } else if (!file && clearBtn) {
      clearBtn.remove();
    }
    // Slide the kind/label/submit metadata row into view only once a file
    // is actually picked. Keeps the empty state visually quiet — just a
    // drop-zone — and surfaces the rest of the form when relevant.
    const meta = this.el.querySelector('[data-upload-meta]');
    if (meta) meta.hidden = !file;
  }

  async _upload(e) {
    e.preventDefault();
    const form = e.target;
    clearFormError(form);
    const fd = new FormData(form);
    const original = fd.get('file');
    if (!(original instanceof File) || original.size === 0) {
      showFormError(form, t('property.documents.errors.file_required'), { field: 'file' });
      return;
    }
    this.setState({ busy: true });
    try {
      const created = await api.post(this.basePath, fd);
      // Show the new file immediately: append the returned row if the API
      // gives us one, otherwise re-fetch. Either way clear busy and refresh
      // counts — no manual page refresh needed.
      if (created && created.id) {
        this.setState({ busy: false, documents: [...(this.state.documents || []), created], inviteSummary: null, error: null });
        this._reportCount();
      } else {
        this.setState({ busy: false, inviteSummary: null, error: null });
        await this._load();
      }
    } catch (err) {
      this.setState({ busy: false });
      const msg = err instanceof ApiError ? err.message : t('property.documents.errors.upload');
      showFormError(form, msg);
    }
  }

  // Authenticated download: fetch as a blob through the api client (so the
  // Bearer header is attached), then trigger a save via a transient anchor.
  async _download(id) {
    const row = (this.state.documents || []).find((d) => d.id === id);
    if (!row) return;
    try {
      const blob = await api.get(`${this.basePath}/${id}/file`, { responseType: 'blob' });
      const url = URL.createObjectURL(blob);
      this._blobUrls.push(url);
      const a = document.createElement('a');
      a.href = url;
      a.download = row.original_name || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('property.documents.errors.download') });
    }
  }

  async _delete(id) {
    if (!confirm(t('property.documents.delete_confirm'))) return;
    try {
      await api.delete(`${this.basePath}/${id}`);
      // Drop it from the list in place (keeps the view; refreshes counts).
      this.setState({ documents: (this.state.documents || []).filter((d) => d.id !== id), error: null });
      this._reportCount();
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('property.documents.errors.delete') });
    }
  }

  // Reading the owner subform as DOM avoids losing focus/typing state on
  // every keystroke; we only re-render on add/remove.
  _readOwnersFromDom() {
    const rows = this.el.querySelectorAll('[data-owner-row]');
    const out = [];
    rows.forEach((row) => {
      const name = row.querySelector('[data-owner-field="name"]')?.value?.trim() || '';
      const share = row.querySelector('[data-owner-field="share"]')?.value?.trim() || '';
      const address = row.querySelector('[data-owner-field="address"]')?.value?.trim() || '';
      const email = row.querySelector('[data-owner-field="email"]')?.value?.trim() || '';
      if (name || share || address || email) out.push({ name, share, address, email });
    });
    return out;
  }

  _addOwnerRow() {
    const row = (this.state.documents || []).find((d) => d.id === this.state.editingFieldsId);
    if (!row) return;
    const current = this._readOwnersFromDom();
    current.push({ name: '', share: '', address: '', email: '' });
    row.extracted_fields = { ...(row.extracted_fields || {}), owners: current };
    this.setState({});
  }

  _removeOwnerRow(i) {
    const row = (this.state.documents || []).find((d) => d.id === this.state.editingFieldsId);
    if (!row) return;
    const current = this._readOwnersFromDom();
    current.splice(i, 1);
    row.extracted_fields = { ...(row.extracted_fields || {}), owners: current };
    this.setState({});
  }

  async _saveFields(e) {
    e.preventDefault();
    const form = e.target;
    clearFormError(form);
    const id = form.getAttribute('data-id');
    const fd = new FormData(form);
    const fields = {
      helyrajzi_szam: String(fd.get('helyrajzi_szam') || '').trim() || undefined,
      address: String(fd.get('address') || '').trim() || undefined,
      area_sqm: fd.get('area_sqm') ? Number(fd.get('area_sqm')) : undefined,
      ownership_form: String(fd.get('ownership_form') || '').trim() || undefined,
      document_date: String(fd.get('document_date') || '').trim() || undefined,
      encumbrances: String(fd.get('encumbrances') || '')
        .split('\n').map((s) => s.trim()).filter(Boolean),
      owners: this._readOwnersFromDom(),
    };
    // Strip undefined/empty so the JSON payload is clean.
    Object.keys(fields).forEach((k) => {
      const v = fields[k];
      if (v === undefined || (Array.isArray(v) && v.length === 0)) delete fields[k];
    });
    try {
      await api.patch(`${this.basePath}/${id}`, { extracted_fields: fields });
      this.setState({ editingFieldsId: null, documents: null, error: null });
      this._load();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : t('property.documents.errors.save');
      showFormError(form, msg);
    }
  }

  // Import the owners[] payload into property_external_owners as
  // record-of-deed entries. Owners on the tulajdoni lap are legal
  // owners, not Nestly users — granting account access is a separate,
  // deliberate action handled by MembersPanel (Hozzáférés).
  // Server-side dedup means re-importing is safe; the response carries
  // only the newly-created rows.
  async _importOwners(docId) {
    const row = (this.state.documents || []).find((d) => d.id === docId);
    const owners = row?.extracted_fields?.owners;
    if (!Array.isArray(owners) || owners.length === 0) return;
    this.setState({ inviteBusyId: docId, inviteSummary: null, error: null });
    try {
      const res = await api.post(
        `/properties/${this.propertyId}/external-owners/from-document/${docId}`,
        {},
      );
      const created = Array.isArray(res?.data) ? res.data.length : 0;
      const summary = created > 0
        ? t('property.documents.import_summary.ok', { count: String(created) })
        : t('property.documents.import_summary.none');
      this.setState({ inviteBusyId: null, inviteSummary: summary });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : t('property.documents.import_summary.failed');
      this.setState({ inviteBusyId: null, error: msg });
    }
  }
}

export function attachDocumentsPanel(rootEl, props) {
  const slot = rootEl.querySelector((props && props.slot) || '[data-documents-slot]');
  if (!slot) return null;
  const panel = new DocumentsPanel(props);
  panel.render(slot);
  return panel;
}
